import React, { useCallback, useEffect, useState } from 'react';
import {
  getFeatureFleetSummary,
  listFeatureFleet,
  queueFeatureEncode,
  bulkAttachFeatureSources,
  getMasterUploadUrl,
} from './adminApi';

// The Screening Room: readiness of all 1,393 programmed screens. A film is
// READY when its licensed master has been encoded into the encrypted
// 4-rendition package and published to StarView Open Connect - until then
// its showtimes play the trailer fallback. The moment a sales agent
// delivers a master, attach its URL here (or paste a whole delivery
// manifest) and the encoder takes it from URL to live feature untouched.

const STATUS_META = {
  ready: { label: 'Ready', cls: 'approved' },
  encoding: { label: 'Encoding', cls: 'pending' },
  queued: { label: 'Queued', cls: 'pending' },
  failed: { label: 'Failed', cls: 'rejected' },
  none: { label: 'No master', cls: 'rejected' },
};

function Tile({ label, value, tone }) {
  return (
    <div className={`adm-stat-card adm-stat-card--${tone}`}>
      <div className="adm-stat-value">{value}</div>
      <div className="adm-stat-label">{label}</div>
    </div>
  );
}

function Row({ movie, secret, onChanged }) {
  const [sourceUrl, setSourceUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const meta = STATUS_META[movie.encodeStatus] || STATUS_META.none;

  const attach = async () => {
    if (!/^https?:\/\//.test(sourceUrl.trim())) { setError('Paste an https master URL first'); return; }
    setBusy(true); setError(null);
    try {
      await queueFeatureEncode(movie.moviePublicId, sourceUrl.trim(), secret);
      setSourceUrl('');
      onChanged();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true); setError(null);
    try { await queueFeatureEncode(movie.moviePublicId, undefined, secret); onChanged(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const uploadLink = async () => {
    setBusy(true); setError(null);
    try {
      const { uploadUrl, publicUrl } = await getMasterUploadUrl(movie.moviePublicId, secret);
      await navigator.clipboard.writeText(uploadUrl).catch(() => {});
      window.prompt('24h presigned PUT URL (copied). After upload, the master lives at the URL below - attach it as the source:', publicUrl);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="adm-detail-card" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ minWidth: 240 }}>
          <strong>{movie.movieTitle}</strong>
          <span className={`adm-status-pill adm-status-${meta.cls}`} style={{ marginLeft: 10 }}>{meta.label}</span>
          <div className="adm-section-note" style={{ marginTop: 2 }}>
            {movie.country} · {movie.moviePublicId}
            {movie.renditions?.length ? ` · ${movie.renditions.join('/')}` : ''}
          </div>
          {movie.encodeError && <div className="adm-section-note" style={{ color: '#ff8080' }}>{movie.encodeError}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {movie.encodeStatus === 'failed' ? (
            <button className="adm-review-btn" disabled={busy} onClick={retry}>↻ Retry encode</button>
          ) : movie.encodeStatus === 'none' ? (
            <>
              <input
                style={{ width: 300, padding: '7px 10px', borderRadius: 8, border: '1px solid #333', background: '#14142a', color: '#fff' }}
                placeholder="https://… master file URL from the sales agent"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
              />
              <button className="adm-review-btn" disabled={busy} onClick={attach}>{busy ? '…' : '🎞 Encode'}</button>
              <button className="adm-review-btn" disabled={busy} onClick={uploadLink} title="Presigned R2 upload for a local master file">⇪ Upload link</button>
            </>
          ) : null}
        </div>
      </div>
      {error && <p className="adm-section-note" style={{ color: '#ff8080', marginTop: 6 }}>{error}</p>}
    </div>
  );
}

export default function ScreeningRoomPanel({ secret }) {
  const [summary, setSummary] = useState(null);
  const [movies, setMovies] = useState([]);
  const [matched, setMatched] = useState(0);
  const [status, setStatus] = useState('none');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    Promise.all([
      getFeatureFleetSummary(secret),
      listFeatureFleet({ status, search }, secret),
    ])
      .then(([s, l]) => { setSummary(s); setMovies(l.movies); setMatched(l.matched); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [secret, status, search]);

  useEffect(() => { load(); }, [load]);

  const runBulk = async () => {
    setBulkResult(null);
    try {
      const entries = JSON.parse(bulkText);
      const result = await bulkAttachFeatureSources(entries, secret);
      setBulkResult(`${result.queued} queued, ${result.rejected} rejected`);
      load();
    } catch (err) {
      setBulkResult(`Error: ${err.message}`);
    }
  };

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Screening Room</h1>
          <p className="adm-page-subtitle">
            Feature readiness across every programmed screen. Attach a delivered master's URL and the
            pipeline runs it to an encrypted, edge-served feature automatically; until then that film's
            showtimes play its trailer.
          </p>
        </div>
        <button className="adm-review-btn" onClick={load}>↻ Refresh</button>
      </div>

      {summary && (
        <div className="adm-stats-grid">
          <Tile label="Ready to screen" value={summary.ready} tone="approved" />
          <Tile label="Encoding / queued" value={summary.encoding + summary.queued} tone="pending" />
          <Tile label="Failed" value={summary.failed} tone="rejected" />
          <Tile label="Awaiting a master" value={summary.noSource} tone="total" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        {['none', 'queued', 'encoding', 'failed', 'ready'].map((s) => (
          <button key={s} className="adm-review-btn" onClick={() => setStatus(s)} style={status === s ? {} : { opacity: 0.5 }}>
            {STATUS_META[s].label}
          </button>
        ))}
        <input
          style={{ flex: 1, minWidth: 200, padding: '8px 10px', borderRadius: 8, border: '1px solid #333', background: '#14142a', color: '#fff' }}
          placeholder="Search title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="adm-review-btn" onClick={() => setBulkOpen((v) => !v)}>📦 Bulk manifest</button>
      </div>

      {bulkOpen && (
        <div className="adm-detail-card" style={{ marginBottom: 14 }}>
          <p className="adm-section-note">
            Paste a delivery manifest: <code>{'[{"movieId":"india-theater-4","sourceUrl":"https://…"}]'}</code> (up to 200 per batch).
          </p>
          <textarea
            style={{ width: '100%', minHeight: 110, borderRadius: 8, border: '1px solid #333', background: '#14142a', color: '#fff', padding: 10, fontFamily: 'monospace' }}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <button className="adm-review-btn" onClick={runBulk}>Queue batch</button>
            {bulkResult && <span className="adm-section-note">{bulkResult}</span>}
          </div>
        </div>
      )}

      {loading ? (
        <p className="adm-section-note">Loading…</p>
      ) : error ? (
        <p className="adm-section-note" style={{ color: '#ff8080' }}>{error}</p>
      ) : (
        <>
          <p className="adm-section-note">{matched.toLocaleString()} screen(s) match</p>
          {movies.map((m) => <Row key={m.moviePublicId} movie={m} secret={secret} onChanged={load} />)}
        </>
      )}
    </div>
  );
}
